import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [stats, setStats] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [autoResponseStatus, setAutoResponseStatus] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get('/api/courses', { params: { search: searchTerm } });
        setCourses(response.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };
    if (searchTerm) fetchCourses();
    else setCourses([]);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedCourse) {
      axios.get(`/api/discussions/${selectedCourse.id}`)
        .then(response => setDiscussions(response.data))
        .catch(error => console.error('Error fetching discussions:', error));
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedCourse && selectedDiscussion) {
      axios.get(`/api/discussion-stats/${selectedCourse.id}/${selectedDiscussion.id}`)
        .then(response => setStats(response.data))
        .catch(error => console.error('Error fetching stats:', error));
    }
  }, [selectedCourse, selectedDiscussion]);

  const handleChangeCourse = () => {
    setSelectedCourse(null);
    setSelectedDiscussion(null);
    setDiscussions([]);
    setStats(null);
    setChatResponse('');
    setAutoResponseStatus('');
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput || !stats?.posts) return;
    try {
      const response = await axios.post('/api/openai-chat', {
        prompt: chatInput,
        posts: stats.posts,
      });
      setChatResponse(response.data.response);
      console.log('CHAT RESPONSE:', JSON.stringify(chatResponse));
      setChatInput('');
    } catch (error) {
      console.error('Error querying OpenAI:', error);
      setChatResponse('Failed to get response from OpenAI');
    }
  };

  const handleAutoRespond = async () => {
    if (!selectedCourse || !selectedDiscussion || !stats?.posts) return;
    try {
      setAutoResponseStatus('Processing...');
      const response = await axios.post(`/api/auto-respond/${selectedCourse.id}/${selectedDiscussion.id}`, {
        posts: stats.posts,
      });
      setAutoResponseStatus(response.data.message);
    } catch (error) {
      console.error('Error auto-responding:', error);
      setAutoResponseStatus('Failed to auto-respond');
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Discussion Tools</h1>
        {selectedCourse && (
          <button className="return-button" onClick={handleChangeCourse}>
            Back to Courses
          </button>
        )}
      </header>
      <div className="container">
        {!selectedCourse ? (
          <div className="course-selection">
            <h2>Choose a Course to Analyze</h2>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Enter course name to search..."
            />
            {courses.length > 0 && (
              <ul className="course-list">
                {courses.map(course => (
                  <li key={course.id} onClick={() => setSelectedCourse(course)}>
                    {course.name} (ID: {course.id})
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <>
            <aside className="sidebar">
              <h2>Course Discussions</h2>
              <ul className="discussion-list">
                {discussions.map(disc => (
                  <li
                    key={disc.id}
                    className={selectedDiscussion?.id === disc.id ? 'active' : ''}
                    onClick={() => setSelectedDiscussion(disc)}
                  >
                    {disc.title}
                  </li>
                ))}
              </ul>
            </aside>
            <main className="main-content">
              {selectedDiscussion ? (
                <>
                  <section className="card">
                    <h3>Discussion Insights</h3>
                    {stats ? (
                      <div className="stats-container">
                        <div className="stats-column">
                          <p><strong>Total Students:</strong> {stats.totalStudents}</p>
                          <p><strong>Students Replied to Initial Post:</strong> {stats.initialReplies}</p>
                          <p><strong>Students Replied to Classmates:</strong> {stats.classmateReplies}</p>
                        </div>
                        <div className="stats-column">
                          <p><strong>Total Posts:</strong> {stats.totalPosts}</p>
                          <p><strong>Average Posts per Student:</strong> {stats.avgPostsPerStudent}</p>
                          <p><strong>Average Post Length (chars):</strong> {stats.avgPostLength}</p>
                        </div>
                      </div>
                    ) : (
                      <p>Loading statistics...</p>
                    )}
                  </section>
                  <section className="card">
                    <h3>AI Response Generator</h3>
                    <p className="description">This tool automatically generates thoughtful AI responses to all initial discussion posts, providing evaluations and follow-up questions to enhance student engagement. Responses are posted only once per thread to avoid repetition.</p>
                    <button onClick={handleAutoRespond}>Generate AI Replies</button>
                    {autoResponseStatus && <p className="status">{autoResponseStatus}</p>}
                  </section>
                  <section className="card">
                    <h3>AI Analysis</h3>
                    <form onSubmit={handleChatSubmit}>
                      <textarea
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        placeholder="Type your question for AI analysis..."
                        rows="3"
                      />
                      <button type="submit">Send</button>
                    </form>
                    {chatResponse && (
                      <div className="chat-response">
                        <h4>Response:</h4>
                        <div style={{ whiteSpace: 'pre-line' }}>{chatResponse}</div>
                      </div>
                    )}
                  </section>
                </>
              ) : (
                <p className="placeholder">Select a discussion to view tools</p>
              )}
            </main>
          </>
        )}
      </div>
    </div>
  );
}

export default App;
